/**
 * HeroAssessmentCard
 * Embeds the LLM-based Skills Assessment in the homepage hero section.
 * Visitors complete the assessment unauthenticated (signed in anonymously)
 * and see a gated results screen (AssessmentResultsReveal) prompting account creation.
 * On registration the anonymous Firebase account is linked to the real account,
 * preserving the UID and all assessment / learning-plan data.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ClipboardList } from 'lucide-react';
import SkillsAssessmentChat from './SkillsAssessmentChat';
import { AssessmentResultsReveal } from './AssessmentResultsReveal';
import { signInAnonymousUser } from '../../services/firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { AssessmentResult } from '../../services/skillsAssessment';

type CardStep = 'intro' | 'assessing' | 'gated';


const CardHeader: React.FC = () => (
  <div
    className="p-6 rounded-t-2xl"
    style={{ background: 'linear-gradient(135deg, #5b6cff 0%, #8b97ff 100%)' }}
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <ClipboardList className="w-5 h-5 text-white" />
      </div>
      <h3 className="font-montserrat font-bold text-xl text-white leading-tight">
        Your Free Skills Assessment
      </h3>
    </div>
    <p className="text-white/80 text-sm ml-12">
      Under 10 minutes · No jargon · No wrong answers
    </p>
  </div>
);

export const HeroAssessmentCard: React.FC = () => {
  const [step, setStep] = useState<CardStep>('intro');
  const [isStarting, setIsStarting] = useState(false);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<AssessmentResult['extractedData'] | undefined>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const isRealUser = isAuthenticated && user?.emailVerified;

  useEffect(() => {
    if (assessmentId) {
      sessionStorage.setItem('heroAssessmentId', assessmentId);
    }
  }, [assessmentId]);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      if (!isAuthenticated) {
        const result = await signInAnonymousUser();
        if (!result.success) {
          navigate('/register', { state: { from: { pathname: '/skills-assessment' } } });
          return;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 600));
      }
      setStep('assessing');
    } catch (err) {
      console.error('Failed to start assessment:', err);
      navigate('/register', { state: { from: { pathname: '/skills-assessment' } } });
    } finally {
      setIsStarting(false);
    }
  };

  const handleComplete = (id: string, data?: AssessmentResult['extractedData']) => {
    sessionStorage.setItem(
      'heroAssessmentAnswers',
      JSON.stringify({ assessmentId: id, completedAt: new Date().toISOString() })
    );
    setAssessmentId(id);
    setExtractedData(data);
    setStep('gated');
  };

  /* ------------------------------------------------------------------ */
  /* INTRO                                                                 */
  /* ------------------------------------------------------------------ */
  if (step === 'intro') {
    return (
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
        <CardHeader />
        <div className="p-6 space-y-5">
          <p className="text-neutral-600 font-open-sans text-sm leading-relaxed">
            Answer a few quick questions and we'll build your personalised AI
            learning plan — matched to your goals, your role, and your schedule.
          </p>

          <ul className="space-y-2">
            {['Takes under 10 minutes', 'No wrong answers', 'Instantly personalised to you'].map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-neutral-600">
                <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#00c2a8' }} />
                {item}
              </li>
            ))}
          </ul>

          {isRealUser ? (
            <Link
              to="/skills-assessment"
              className="block w-full py-3 px-6 rounded-full text-white font-montserrat font-semibold text-center transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #5b6cff, #00c2a8)' }}
            >
              Go to My Skills Assessment →
            </Link>
          ) : (
            <button
              onClick={handleStart}
              disabled={isStarting}
              className="w-full py-3 px-6 rounded-full text-white font-montserrat font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #5b6cff, #00c2a8)' }}
            >
              {isStarting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Starting…
                </>
              ) : (
                'Start Free Assessment →'
              )}
            </button>
          )}

          <p className="text-xs text-center text-neutral-400">
            No account needed · 100% free · Cancel anytime
          </p>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* ASSESSING                                                             */
  /* ------------------------------------------------------------------ */
  if (step === 'assessing') {
    return (
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[620px]">
        <CardHeader />
        <div className="flex-1 min-h-0 overflow-hidden">
          <SkillsAssessmentChat
            onAssessmentComplete={handleComplete}
            className="h-full max-h-[520px]"
          />
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* GATED RESULTS                                                         */
  /* ------------------------------------------------------------------ */
  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
      <CardHeader />
      <div className="p-6">
        <AssessmentResultsReveal
          assessmentId={assessmentId!}
          extractedData={extractedData}
          variant="card"
          registerHref="/register"
        />
      </div>
    </div>
  );
};

export default HeroAssessmentCard;
